export type Nullable<T> = T | null;

// https://github.com/piotrwitek/utility-types/blob/df2502ef504c4ba8bd9de81a45baef112b7921d0/src/mapped-types.ts#L539-L559
export type Optional<T extends object, K extends keyof T = keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type RequiredProps<T extends object, K extends keyof T = keyof T> = Omit<T, K> & Required<Pick<T, K>>;

export type NullableProps<T extends object, K extends keyof T = keyof T> = { [P in keyof T]: Nullable<T[P]> };

export type KeysOfUnion<T> = T extends T ? keyof T : never;

/**
 * @see https://www.totaltypescript.com/concepts/the-prettify-helper
 */
export type Prettify<T> = {
	[K in keyof T]: T[K];
} & {};

/**
 * DiscriminateUnion
 * @desc Given a discriminated union and the key and value of the discriminant, produces the single relevant constituent of the union
 * @see https://stackoverflow.com/a/50125960
 * @param T - a discriminated union
 * @param K - discriminant key
 * @param V - discriminant value
 * @example
 *   interface T1 { key: 'foo' };
 *   interface T2 { key: 'bar' };
 *   type U = T1 | T2;
 *
 *   // Expect: T1
 *   DiscriminateUnion<U, 'key', 'foo'>;
 *
 *   // Expect: T2
 *   DiscriminateUnion<U, 'key', 'bar'>;
 */
export type DiscriminateUnion<T, K extends keyof T, V extends T[K]> = T extends Record<K, V> ? T : never;

/**
 * MapDiscriminatedUnion
 * @desc Builds a map from a discriminated union
 * @see https://stackoverflow.com/a/50125960
 * @param T - a discriminated union
 * @param K - discriminant key
 * @example
 *   interface T1 { key: 'foo' };
 *   interface T2 { key: 'bar' };
 *   type U = T1 | T2;
 *
 *   // Expect: { foo: T1, bar: T2 }
 *   MapDiscriminatedUnion<U, 'key'>;
 */
export type MapDiscriminatedUnion<T extends Record<K, string>, K extends keyof T> = {
	[V in T[K]]: DiscriminateUnion<T, K, V>;
};

export type Nullish = null | undefined;
export const isNullish = (input: unknown): input is Nullish => input === null || typeof input === 'undefined';
export const isNotNullish = <T>(input: T | Nullish): input is T => !isNullish(input);
export const isUndefined = (input: unknown): input is undefined => typeof input === 'undefined';
export const isNotUndefined = <T>(input: T | Nullish): input is T | null => !isUndefined(input);
